import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { logger } from 'shared/lib'
import { RequestLoadData } from 'shared/api/lib'
import {
  type Integration,
  IntegrationKey,
  IntegrationsApi,
  type IResponseIntegration,
  type IResponseUserIntegration,
} from 'entities/Integrations'
import { CATEGORY_LABELS } from 'entities/Integrations/lib/integrationsDescription'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { ManageIntegrationConnectionStore } from 'features/ManageIntegrationConnection'
import { integrationSortOrder } from 'pages/settings/pages/integrations/lib/integrationSortOrder'
import { IntegrationWebhookStore } from 'pages/settings/pages/integrations/store/IntegrationWebhookStore'

export class IntegrationListStore {
  private _isCategoriesLoading = false
  private _categories: string[] = []
  private _selectedCategory = ''

  private _search = ''

  private _isIntegrationListLoading = false
  private _integrationsMap: Map<string, Integration> = new Map()

  private _disposeFetchIntegrations: IReactionDisposer | null = null
  private _waitingForIntegrationDisconnectedUnsubscribe: (() => void) | null = null

  private _manageIntegrationConnectionStore = new ManageIntegrationConnectionStore()
  private _integrationWebhookStore: IntegrationWebhookStore

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)
    this._integrationWebhookStore = new IntegrationWebhookStore(this._entities)

    this._reactionFetchIntegrations()
    void this._init()
  }

  private _reactionFetchIntegrations = () => {
    this._disposeFetchIntegrations?.()

    this._disposeFetchIntegrations = reaction(
      () => [this.selectedCategory, this._search],
      () => {
        this._requestLoadIntegrations.debounceLoadData({
          category: this.selectedCategory,
          search: this._search,
        })
      }
    )
  }

  private _init = async () => {
    await this._fetchCategories()
    this.setActiveCategory(this._categories[0] || '')
  }

  private _clearReactions = () => {
    this._disposeFetchIntegrations?.()
  }

  private _mapIntegrations = (responseIntegrations: IResponseIntegration[]) => {
    this._integrationsMap = new Map()
    responseIntegrations
      .sort(
        (a, b) =>
          (integrationSortOrder[a.key] ?? Infinity) - (integrationSortOrder[b.key] ?? Infinity)
      )
      .forEach((integrationResponse) => {
        const integrationFromStore = this._entities.integrationsStore.getIntegration(
          integrationResponse.key
        )

        if (integrationFromStore) {
          integrationFromStore.syncOrigin(integrationResponse)
          this._integrationsMap.set(integrationFromStore.key, integrationFromStore)
        }
      })
  }

  private _requestLoadIntegrations = new RequestLoadData<{
    category?: string
    search?: string
  }>({
    requestCallback: async ({ cancelRequest }, props) => {
      const category = props?.category
      const search = props?.search

      runInAction(() => {
        this._isIntegrationListLoading = true
      })

      const {
        data: { data: integrations },
      } = await IntegrationsApi.getIntegrations(
        { category, search },
        {
          cancelToken: cancelRequest.token,
        }
      )

      this._mapIntegrations(integrations)
    },
    finallyCallback: () => {
      runInAction(() => {
        this._isIntegrationListLoading = false
      })
    },
  })

  private _fetchIntegrations = async (category?: string, search?: string) => {
    return this._requestLoadIntegrations.loadData({
      category: category,
      search: search,
    })
  }

  private _fetchCategories = async () => {
    try {
      runInAction(() => {
        this._isCategoriesLoading = true
      })

      const {
        data: { data },
      } = await IntegrationsApi.getIntegrationsCategory()
      runInAction(() => {
        this._categories = data
      })
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._isCategoriesLoading = false
      })
    }
  }

  setActiveCategory = (categoryKey: string) => {
    this._selectedCategory = categoryKey
    this._search = ''
  }

  setSearch = (search: string) => {
    this._search = search
  }

  onDisconnect = async (integration: Integration, isSilentDisconnect?: boolean) => {
    await this._manageIntegrationConnectionStore.disconnect(integration, isSilentDisconnect)
  }

  onReconnect = async (integration: Integration) => {
    try {
      runInAction(() => {
        integration.setLoading(true)
      })

      if (integration.key === IntegrationKey.activecampaign) {
        await this.onDisconnect(integration, true)
      }

      await this._manageIntegrationConnectionStore.reconnect(integration)
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        integration.setLoading(false)
      })
    }
  }

  onViewWebhook = async (integration: Integration) => {
    this._integrationWebhookStore.openIntegrationWebhookModal(integration)
  }

  syncUserIntegration = (userIntegrationResponse: IResponseUserIntegration) => {
    const integrationFromStore = this._entities.integrationsStore.getIntegration(
      userIntegrationResponse.integration.key
    )

    if (integrationFromStore) {
      integrationFromStore.syncUserIntegration(userIntegrationResponse)

      const localIntegration = this._integrationsMap.get(userIntegrationResponse.integration.key)
      if (localIntegration && localIntegration !== integrationFromStore) {
        this._integrationsMap.set(integrationFromStore.key, integrationFromStore)
      }
    }
  }

  get isIntegrationListLoading() {
    return this._isIntegrationListLoading
  }

  get integrationList() {
    return Array.from(this._integrationsMap.values()).filter((integration) => {
      if (
        !this._entities.featureFlagsStore.salesmsg_design_v2_calendly_page &&
        integration.key === 'calendly'
      ) {
        return false
      }
      if (!this._entities.featureFlagsStore.zoho_integration && integration.key === 'zoho') {
        return false
      }
      return integration.is_active
    })
  }

  get isEmptyList() {
    return (
      !this.isCategoriesLoading && !this.isIntegrationListLoading && !this.integrationList.length
    )
  }

  get categories(): {
    key: string
    label: string
    isBeta?: boolean
  }[] {
    return this._categories.map((key) => ({
      key,
      label: CATEGORY_LABELS[key] ?? key,
      isBeta: key === 'agents',
    }))
  }

  get selectedCategory() {
    return this._selectedCategory
  }

  get isCategoriesLoading() {
    return this._isCategoriesLoading
  }

  get search() {
    return this._search
  }

  get currentListTitle() {
    if (this._search !== '') {
      return 'Search results'
    }
    return CATEGORY_LABELS[this._selectedCategory]
  }

  get availableIntegrationsForWebhook() {
    return this._integrationWebhookStore.availableIntegrationsForWebhook
  }

  dispose = () => {
    this._requestLoadIntegrations.dispose()
    this._clearReactions()
    this._waitingForIntegrationDisconnectedUnsubscribe?.()
    this._waitingForIntegrationDisconnectedUnsubscribe = null
    this._integrationWebhookStore.dispose()
  }
}
